/* eslint-disable */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Home as HomeIcon,
  Users,
  ShieldCheck,
  ScrollText,
  Building2,
  ArrowRight,
  Sparkles,
  MapPin,
  Mail,
  Calendar,
  Vote,
  Wrench,
} from "lucide-react";
import ContactForm from "./ContactForm";
import FeaturedProperties from "./properties/FeaturedProperties";
import "./home.css";

function animateCounter(el, target, duration = 1400) {
  const startTs = performance.now();
  const tick = (now) => {
    const progress = Math.min((now - startTs) / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    el.textContent = Math.floor(eased * target).toLocaleString();
    if (progress < 1) requestAnimationFrame(tick);
    else el.textContent = target.toLocaleString();
  };
  requestAnimationFrame(tick);
}

const FEATURES = [
  {
    Icon: ShieldCheck,
    title: "Resident-led & transparent",
    body: "Owners run the society — every decision, document and approval is visible to all members.",
    color: "var(--accent)",
    bg: "#dcfce7",
  },
  {
    Icon: Users,
    title: "Community-first",
    body: "From RWA matters to cultural events, the society exists to make life inside Vishal Sanjivini better.",
    color: "#7C3AED",
    bg: "#ede9fe",
  },
  {
    Icon: ScrollText,
    title: "Documents in one place",
    body: "Bye-laws, GBM minutes, financials, RERA certificates — searchable, versioned, downloadable.",
    color: "#1565C0",
    bg: "#dbeafe",
  },
  {
    Icon: Vote,
    title: "Voting & approvals",
    body: "Board approves member changes; documents go through review. Every move logged for audit.",
    color: "#D97706",
    bg: "#fef3c7",
  },
];

const STATS = [
  { num: 338, label: "Luxury villas" },
  { num: 34, label: "Acres landscaped" },
  { num: 4, label: "BHK + theatre" },
  { num: 2025, label: "Registered" },
];

const GALLERY_SLIDES = [
  { file: "community-street.jpg", caption: "Street · Vishal Sanjivini" },
  { file: "community-park.jpg",   caption: "Community Park · Vishal Sanjivini" },
  { file: "community-clubhouse.jpg", caption: "Clubhouse · Vishal Sanjivini" },
  { file: "community-gate.jpg",   caption: "Main Gate · Vishal Sanjivini" },
];

export default function Home() {
  const [galleryIdx, setGalleryIdx] = useState(0);
  const galleryPausedRef = useRef(false);
  const galleryTimerRef = useRef(null);

  const galleryNext = useCallback(() => setGalleryIdx((i) => (i + 1) % GALLERY_SLIDES.length), []);
  const galleryPrev = useCallback(() => setGalleryIdx((i) => (i - 1 + GALLERY_SLIDES.length) % GALLERY_SLIDES.length), []);

  const restartGalleryTimer = useCallback(() => {
    if (galleryTimerRef.current) clearInterval(galleryTimerRef.current);
    galleryTimerRef.current = setInterval(() => {
      if (!galleryPausedRef.current) setGalleryIdx((i) => (i + 1) % GALLERY_SLIDES.length);
    }, 5000);
  }, []);

  useEffect(() => {
    restartGalleryTimer();
    return () => { if (galleryTimerRef.current) clearInterval(galleryTimerRef.current); };
  }, [restartGalleryTimer]);

  const handleScroll = (e, id) => {
    e.preventDefault();
    const el = document.querySelector(id);
    if (el) {
      const yOffset = -80;
      const y = el.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const statRefs = useRef([]);

  useEffect(() => {
    const section = document.querySelector("#vs-stats");
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            statRefs.current.forEach((el, idx) => {
              if (el) animateCounter(el, STATS[idx].num);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.35 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="vs-home">
      {/* ============ HERO ============ */}
      <section className="vs-hero">
        <img
          src={`${process.env.PUBLIC_URL || ""}/favicon-512x512.png`}
          onError={(e) => {
            e.currentTarget.src = `${process.env.PUBLIC_URL || ""}/android-chrome-512x512.png`;
          }}
          alt=""
          aria-hidden="true"
          className="vs-hero-seal"
        />
        <div className="container vs-hero-grid">
          <div>
            <div className="vs-hero-eyebrow">
              <Sparkles size={12} strokeWidth={2.4} />
              VSMACS · Resident-driven · Est. 2025
            </div>
            <h1 className="vs-hero-title">
              A <span>home</span>owners' platform for <br /> Vishal Sanjivini
            </h1>
            <p className="vs-hero-sub">
              Built by the 338 villa owners of our gated community in Tukkuguda, Hyderabad —
              the Vishal Sanjivini Villa Owners Maintenance Mutually Aided Cooperative
              Society runs on transparency, member voice, and shared stewardship.
            </p>

            <div className="vs-hero-actions">
              <a
                href="#about"
                onClick={(e) => handleScroll(e, "#about")}
                className="vs-btn-hero-primary"
              >
                Know More <ArrowRight size={16} />
              </a>
              <a
                href="#contact"
                onClick={(e) => handleScroll(e, "#contact")}
                className="vs-btn-hero-ghost"
              >
                <Mail size={15} /> Contact the committee
              </a>
            </div>

            <p className="vs-hero-disclaimer">
              <strong>Disclaimer:</strong> This is an independent website created and
              managed by the homeowners. It is{" "}
              <strong>
                not affiliated with, endorsed by, or managed by Vishal Projects Pvt. Ltd.
              </strong>{" "}
              or any of its representatives.
            </p>
          </div>

          <div className="vs-hero-card">
            <img
              src={`${process.env.PUBLIC_URL || ""}/favicon-64x64.png`}
              alt=""
              aria-hidden="true"
              className="vs-hero-card-seal"
            />
            <h3>Vishal Sanjivini</h3>
            <span className="vs-hero-card-tag">Project at a glance</span>
            <ul className="vs-hero-card-list">
              <li>
                <span>Location</span> <strong>Tukkuguda</strong>
              </li>
              <li>
                <span>Project area</span> <strong>34 acres</strong>
              </li>
              <li>
                <span>Total villas</span> <strong>338</strong>
              </li>
              <li>
                <span>Typical unit</span> <strong>4 BHK + Theatre</strong>
              </li>
              <li>
                <span>RERA</span> <strong>P02400000533</strong>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ============ COMMUNITY GALLERY ============ */}
      <section
        className="vs-gallery"
        role="region"
        aria-label="Community gallery"
        onMouseEnter={() => { galleryPausedRef.current = true; }}
        onMouseLeave={() => {
          setTimeout(() => { galleryPausedRef.current = false; }, 2000);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowRight") { galleryNext(); restartGalleryTimer(); }
          if (e.key === "ArrowLeft")  { galleryPrev(); restartGalleryTimer(); }
        }}
        tabIndex={0}
      >
        <div
          className="vs-gallery-track"
          style={{ transform: `translateX(-${galleryIdx * 100}%)` }}
        >
          {GALLERY_SLIDES.map((s, i) => (
            <div className="vs-gallery-slide" key={s.file}>
              <img
                src={`${process.env.PUBLIC_URL || ""}/illustrations/${s.file}`}
                alt={`${s.caption} — aerial community illustration`}
                className="vs-gallery-img"
                loading={i === 0 ? "eager" : "lazy"}
              />
              <div className="vs-gallery-vignette" />
              <span className="vs-gallery-caption">{s.caption}</span>
            </div>
          ))}
        </div>
        <div className="vs-gallery-dots" role="tablist">
          {GALLERY_SLIDES.map((s, i) => (
            <button
              key={i}
              role="tab"
              aria-selected={i === galleryIdx}
              aria-label={`Go to slide ${i + 1}`}
              className={`vs-gallery-dot${i === galleryIdx ? " active" : ""}`}
              onClick={() => { setGalleryIdx(i); restartGalleryTimer(); }}
            />
          ))}
        </div>
      </section>

      {/* ============ STATS STRIP ============ */}
      <section id="vs-stats" className="vs-stats">
        <div className="container vs-stats-grid">
          {STATS.map((s, i) => (
            <div key={s.label}>
              <span
                className="vs-stat-num"
                ref={(el) => (statRefs.current[i] = el)}
              >
                0
              </span>
              <div className="vs-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <main id="main">
        {/* ============ WHY THE SOCIETY ============ */}
        <section className="vs-section">
          <div className="container">
            <div className="vs-section-head">
              <span className="vs-section-eyebrow">Why a residents' cooperative</span>
              <h2 className="vs-section-title">
                Built by residents, for residents
              </h2>
              <p className="vs-section-lead">
                A cooperative society is not a builder portal. It's how we
                co-own the upkeep of our streets, the books, the meetings —
                and the future of our community.
              </p>
            </div>

            <div className="vs-features">
              {FEATURES.map(({ Icon, title, body, color, bg }) => (
                <div key={title} className="vs-feature">
                  <span
                    className="vs-feature-icon"
                    style={{ background: bg, color }}
                  >
                    <Icon size={22} strokeWidth={1.8} />
                  </span>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ ABOUT ============ */}
        <section id="about" className="vs-section alt">
          <div className="container">
            <div className="vs-section-head">
              <span className="vs-section-eyebrow">About</span>
              <h2 className="vs-section-title">
                Vishal Sanjivini Villa Owners Maintenance Mutually Aided
                Cooperative Society
              </h2>
              <p className="vs-section-lead">
                Two sides of the same coin — the community we live in, and the
                society we collectively run.
              </p>
            </div>

            <div className="vs-about-grid">
              <article className="vs-about-card">
                <h3>
                  <span className="vs-num">1</span> The community
                </h3>
                <p>
                  <strong>Vishal Sanjivini</strong> is an upscale gated villa
                  community in the vibrant locality of{" "}
                  <strong>Tukkuguda, Hyderabad</strong>, spread across 34 acres
                  of lush green landscape. Developed by{" "}
                  <a
                    href="https://www.vishalprojects.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Vishal Projects Pvt. Ltd.
                  </a>
                  , it offers a blend of luxury, comfort and modern living for
                  families seeking a quietly upscale residence in South
                  Hyderabad.
                </p>
                <ul className="vs-detail-list">
                  <li>
                    <span>
                      <strong>Location.</strong> Tukkuguda, adjacent to
                      Electronic City and Fab City, near ORR Exit No. 14.
                    </span>
                  </li>
                  <li>
                    <span>
                      <strong>Project area.</strong> 34 acres of secure,
                      landscaped community.
                    </span>
                  </li>
                  <li>
                    <span>
                      <strong>Total villas.</strong> 338 luxury villas,
                      primarily 4 BHK + Home Theatre, 3,200 – 4,990+ sq. ft.
                    </span>
                  </li>
                  <li>
                    <span>
                      <strong>RERA.</strong> Registration P02400000533.
                    </span>
                  </li>
                </ul>
              </article>

              <article className="vs-about-card">
                <h3>
                  <span className="vs-num">2</span> The cooperative society
                </h3>
                <p>
                  The <strong>Vishal Sanjivini MACS</strong> is the formally
                  registered, independent, resident-driven body that stewards
                  the day-to-day of our community. It operates separately from
                  the builder, and its activities reflect the{" "}
                  <strong>collective interests of villa owners</strong> — not
                  the developer.
                </p>
                <ul className="vs-detail-list">
                  <li>
                    <span>
                      <strong>Maintenance.</strong> Regular updates and
                      maintenance of shared facilities.
                    </span>
                  </li>
                  <li>
                    <span>
                      <strong>Events.</strong> Coordinating community events
                      and cultural gatherings.
                    </span>
                  </li>
                  <li>
                    <span>
                      <strong>Resident voice.</strong> Addressing resident
                      issues openly and on the record.
                    </span>
                  </li>
                  <li>
                    <span>
                      <strong>Guidelines.</strong> Promoting compliance with
                      community guidelines for a peaceful environment.
                    </span>
                  </li>
                </ul>
              </article>
            </div>
          </div>
        </section>

        {/* ============ FEATURED PROPERTIES ============ */}
        <FeaturedProperties />

        {/* ============ CTA ============ */}
        <section className="vs-cta">
          <div className="container">
            <h2>Already a Vishal Sanjivini owner?</h2>
            <p>
              Sign in to read society documents, RSVP for GBM meetings, raise
              concerns and stay in the loop with the board.
            </p>
            <div className="vs-cta-actions">
              <a href="#/" className="vs-btn-cta">
                Member sign in <ArrowRight size={16} />
              </a>
              <a href="#/downloads" className="vs-btn-cta ghost">
                <ScrollText size={15} /> Public documents
              </a>
            </div>
          </div>
        </section>

        {/* ============ CONTACT ============ */}
        <section id="contact" className="vs-section">
          <div className="container">
            <div className="vs-contact-grid">
              <div className="vs-contact-side">
                <span className="vs-section-eyebrow">Get in touch</span>
                <h3 style={{ margin: "10px 0 14px", fontSize: 28 }}>
                  Talk to the committee
                </h3>
                <p>
                  Questions about membership, document approvals, GBM
                  participation or society finances? A board member will get
                  back to you.
                </p>
                <div className="vs-contact-info">
                  <div className="vs-contact-info-item">
                    <span className="vs-ci-icon">
                      <MapPin size={18} strokeWidth={1.8} />
                    </span>
                    <div>
                      <div className="vs-ci-label">Address</div>
                      <div className="vs-ci-value">
                        <a
                          href="https://www.google.com/maps/dir/?api=1&destination=Vishal+Sanjivini,+Tukkuguda,+Hyderabad+501509"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Vishal Sanjivini, Tukkuguda, Hyderabad 501509
                        </a>
                        <div className="vs-ci-map-link">
                          <a
                            href="https://www.google.com/maps/dir/?api=1&destination=Vishal+Sanjivini,+Tukkuguda,+Hyderabad+501509"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Get directions on Google Maps →
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="vs-contact-info-item">
                    <span className="vs-ci-icon">
                      <Mail size={18} strokeWidth={1.8} />
                    </span>
                    <div>
                      <div className="vs-ci-label">Email</div>
                      <div className="vs-ci-value">
                        <img
                          src="/email-support.png"
                          alt="our support email address"
                          style={{ height: "1em", verticalAlign: "-0.18em" }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="vs-contact-info-item">
                    <span className="vs-ci-icon">
                      <Calendar size={18} strokeWidth={1.8} />
                    </span>
                    <div>
                      <div className="vs-ci-label">Response time</div>
                      <div className="vs-ci-value">
                        Typically within 1–2 working days
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <span className="vs-section-eyebrow">Enquiry form</span>
                <h3 style={{ margin: "10px 0 18px", fontSize: 22 }}>
                  Send us a note
                </h3>
                <ContactForm />
              </div>
            </div>
          </div>
        </section>

        {/* ============ FOOTER NOTE ============ */}
        <footer
          style={{
            background: "#0a1b0d",
            color: "rgba(255,255,255,0.72)",
            textAlign: "center",
            padding: "20px 16px",
            fontSize: 13,
          }}
        >
          <p style={{ margin: 0 }}>
            © 2026 Vishal Sanjivini Villa Owners Maintenance Mutually Aided
            Cooperative Society. A community-run initiative.{" "}
            <strong style={{ color: "var(--primary)" }}>
              Not affiliated with Vishal Projects Pvt. Ltd. or vishalprojects.com.
            </strong>
          </p>
        </footer>
      </main>
    </div>
  );
}
